import {
	AnimatePresence,
	motion,
	useReducedMotion,
	type Transition,
	type Variants,
} from 'framer-motion';
import { ChevronsUp } from 'lucide-react';
import type { SessionCallTranscriptionChatMessage } from '@/components/session-call/sessionCallTranscriptionChat';
import { cn } from '@/lib/utils';
import {
	PRONTUARIO_PANEL_PADDING_X,
	PRONTUARIO_TRANSCRIPTION_BG_CLASS,
	PRONTUARIO_TRANSCRIPTION_SCROLL_FOOTER_CLEARANCE_CLASS,
	PRONTUARIO_VIEW_TRANSITION_EASE,
	PRONTUARIO_VIEW_TRANSITION_SEC,
} from '../../constants/prontuarioTheme';
import { SessionCallTranscriptionChatView } from './SessionCallTranscriptionChatView';

const SHADOW_DOWN = '0 8px 24px rgba(0,0,0,0.35)';
const SHADOW_NONE = '0px 0 24px rgba(0,0,0,0)';

const panelVariants: Variants = {
	enter: { y: '-100%', boxShadow: SHADOW_DOWN },
	center: { y: '0%', boxShadow: SHADOW_DOWN },
	exit: { y: '-100%', boxShadow: SHADOW_NONE },
};

const reducedPanelVariants: Variants = {
	enter: { y: '0%', opacity: 0 },
	center: { y: '0%', opacity: 1 },
	exit: { y: '0%', opacity: 0 },
};

/** Mesmo escurecimento da tela que sai nas transições laterais do prontuário. */
const scrimVariants: Variants = {
	enter: { opacity: 0 },
	animate: { opacity: 0.35 },
	exit: { opacity: 0 },
};

type SessionCallTranscriptionRevealPanelProps = {
	isOpen: boolean;
	messages: SessionCallTranscriptionChatMessage[];
	transcriptionActive?: boolean;
	isProntuarioLiveCaptureEnabled?: boolean;
	stoppedNotice?: string | null;
	getBaselineWordCount?: (messageId: string) => number;
	onClose: () => void;
};

const SessionCallTranscriptionRevealPanel = ({
	isOpen,
	messages,
	transcriptionActive = true,
	isProntuarioLiveCaptureEnabled = true,
	stoppedNotice = null,
	getBaselineWordCount,
	onClose,
}: SessionCallTranscriptionRevealPanelProps) => {
	const reduceMotion = useReducedMotion();

	const transition: Transition = reduceMotion
		? { duration: 0.12, ease: 'easeOut' }
		: {
				duration: PRONTUARIO_VIEW_TRANSITION_SEC,
				ease: [...PRONTUARIO_VIEW_TRANSITION_EASE],
		  };

	return (
		<AnimatePresence initial={false} mode="sync">
			{isOpen ? (
				<>
					{reduceMotion ? null : (
						<motion.div
							key="prontuario-transcription-scrim"
							variants={scrimVariants}
							initial="enter"
							animate="animate"
							exit="exit"
							transition={transition}
							className="pointer-events-none absolute inset-0 z-[9] bg-black"
							aria-hidden
						/>
					)}
					<motion.div
						key="prontuario-transcription-panel"
						variants={
							reduceMotion ? reducedPanelVariants : panelVariants
						}
						initial="enter"
						animate="center"
						exit="exit"
						transition={transition}
						className={cn(
							'pointer-events-auto absolute inset-x-0 bottom-0 top-0 z-10 flex min-h-0 flex-col overflow-hidden',
							PRONTUARIO_TRANSCRIPTION_BG_CLASS,
						)}
					>
						<SessionCallTranscriptionChatView
							messages={messages}
							transcriptionActive={transcriptionActive}
							isProntuarioLiveCaptureEnabled={
								isProntuarioLiveCaptureEnabled
							}
							stoppedNotice={stoppedNotice}
							isPanelOpen={isOpen}
							getBaselineWordCount={getBaselineWordCount}
							className="min-h-0 flex-1"
							scrollClassName={
								PRONTUARIO_TRANSCRIPTION_SCROLL_FOOTER_CLEARANCE_CLASS
							}
						/>

						<footer
							className={cn(
								'relative z-20 shrink-0',
								PRONTUARIO_TRANSCRIPTION_BG_CLASS,
								PRONTUARIO_PANEL_PADDING_X,
								'shadow-[0_-10px_28px_rgba(26,36,48,0.92)]',
							)}
						>
							<div
								className="absolute inset-x-0 top-0 border-t border-white/[0.1]"
								aria-hidden
							/>
							<button
								type="button"
								onClick={onClose}
								aria-label="Fechar transcrição"
								className="flex w-full items-center justify-center gap-2 py-3 text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-slate-100 active:text-white"
							>
								<span>Fechar transcrição</span>
								<ChevronsUp
									className="size-4 shrink-0"
									strokeWidth={2.25}
								/>
							</button>
						</footer>
					</motion.div>
				</>
			) : null}
		</AnimatePresence>
	);
};

export { SessionCallTranscriptionRevealPanel };
